<template>
  <section :class="['section', 'child-view', 'section--process']">
    <div class="wrapper">
      <div class="content">
        <div class="title-area">
          <h1>Full Circle of Design Approach</h1>
          <p class="description">MobiDev offers a full-cycle design development process that includes UX and UI stages. This approach will improve the overall user experience and strong basis for future iterations.</p>
        </div>
        <div class="info-area">
          <h2>What is Effective UX/UI Design</h2>
          <p class="info-description">What the product really needs to be successful</p>
          <div class="ux-ui-holder">
            <img class="ui-ux-schema web" src="../assets/images/svg/ui-ux-design.svg" alt="ui ux design schema">
            <img class="ui-ux-schema mobile" src="../assets/images/svg/ui-ux-design-mobile.svg" alt="ui ux design schema">
          </div>
        </div>
        <div class="info-area">
          <h2>Our Workflow</h2>
          <p class="description">The complete UX process is a tool to increase efficiency. It can save both time and money, as well as contribute to quality of products.</p>
        </div>
        <div class="workflow-block">
          <ul class="workflow-list">
            <li tabindex="0" v-for="(item, index) in workflowItems" :key="index" :class="['workflow-item number-' + index, {'active': selectedWorkflowIndex === index}]" @click="selectWorkflowItem(index)">
              <div v-if="item.marker" class="marker">
                <span class="marker-name">{{ item.marker }}</span>
                <span>part</span>
              </div>
              <div class="workflow-holder">
                <span class="workflow-title">{{ item.title }}</span>
                <ul class="workflow-points">
                  <li v-for="(listItem, i) in item.listItems" :key="i">
                    {{ listItem }}
                  </li>
                </ul>
              </div>
            </li>
          </ul>
        </div>
        <div class="info-block">
          <div class="info-col column-1 order-1 col-more-link">
            <span class="subtitle">Research and Analysis</span>
            <h3>Requirements Analysis and Estimating</h3>
            <p>We use all the input you provide and clarify any necessary details to make a rough estimate for the design stage. We also form a draft product logic scheme which can be used by developers to give a very approximate estimate for implementation.</p>
            <p>Estimates, depending on the type of project and the wishes of the customer, can be made for:
              <ul class="list">
                <li>Fixed price;</li>
                <li>Dedicated team;</li>
                <li>Compared estimate with library components use or custom</li>
              </ul>
            </p>
            <p>We always provide an estimate so as to save time and money of our clients.</p>
            <a
              href="/storage/app/media/1_process_Logical_scheme_and_blocks_md_player.pdf" class="more-link"
              target="_blank">
              Explore example <img src="../assets/images/svg/more-link-icon.svg" alt="more-icon">
            </a>
          </div>
          <div class="info-col column-3 order-1-mobile">
            <span class="subtitle">Research and Analysis</span>
            <h3>Business Analysis</h3>
            <p>One of the earliest stages of software development is business analysis, which provides fuller understanding of your future software product and target audience. It also allows us to give more precise design estimates and accelerate the process.</p>
            <p class="subblocks-description">The outcome of business analysis comprises <b>3 main documents:</b></p>
            <div class="subblocks-holder">
              <div class="subblock">
                <span class="subblock-title">1. Product Vision</span>
                <p>Project structure, goals and mission of your product, promotion, detailed analysis of competitors on the market, unique value proposition, main functionality and description of your target audience.</p>
                <a
                  href="/storage/app/media/2_process_Product%20Vision.pdf"
                  class="more-link"
                  target="_blank">
                  Explore example <img src="../assets/images/svg/more-link-icon.svg" alt="more-icon">
                </a>
              </div>
              <div class="subblock">
                <span class="subblock-title">2. Non-Functional Requirements</span>
                <p>Supported devices, versions of browsers and operating systems, screen resolutions, security requirements, performance requirements, system resources, response time, speed of data processing, test specifications, usability requirements, localization requirements.</p>
                <a
                  href="/storage/app/media/3_process_Non-functional%20requirements_md_people.pdf"
                  class="more-link"
                  target="_blank">
                  Explore example <img src="../assets/images/svg/more-link-icon.svg" alt="more-icon">
                </a>
              </div>
              <div class="subblock">
                <span class="subblock-title">3. User Stories</span>
                <p>Short descriptions of your product's functionality from the user's point of view. As a rule, they are made up as follows: “As a … , I want to …, so that …”</p>
                <a
                  href="/storage/app/media/4_process_Sample_of_User%20Stories-2.pdf"
                  class="more-link"
                  target="_blank">
                  Explore example <img src="../assets/images/svg/more-link-icon.svg" alt="more-icon">
                </a>
              </div>
            </div>
          </div>
          <div class="info-col column-2 order-2">
            <span class="subtitle">Ideation</span>
            <h3>Screens Map and User Flow</h3>
            <p>Screens Map and User Flow are created quickly and easily. Iterative exchange of ideas helps us to find the most advantageous solutions for your product. Research on the target audience and development of the software product's concept are mainly carried out at this stage.</p>
            <div class="image-holder">
              <img class="screens-map" src="../assets/images/screens-map.png" alt="#">
              <img class="user-flow" src="../assets/images/user-flow.png" alt="#">
            </div>
            <div class="text-info">
              <span class="text-part">Goals:</span>
              <p>Communication, generation and exchange of ideas, creation of a rough logic model, clarification of additional requirements.</p>
            </div>
            <div class="block-info">
              <span class="block-title">What you should do at this stage:</span>
              <p class="block-text">Participate in discussions, generate ideas for the future product, model situations of usage.</p>
            </div>
            <div class="block-info">
              <span class="block-title">What you should do at this stage:</span>
              <p class="block-text">Screen Map and User Flow that clarifies the principles of work and logic of interaction between main units (using such prototyping tools as POP and InVision), general understanding of the scope and required resources.</p>
            </div>
          </div>
          <div class="info-col column-2 order-2-mobile">
            <span class="subtitle">Ideation</span>
            <h3>Wireframing</h3>
            <p>This is the structure of your future software product, which is created iteratively. The set of interface elements and product logic are defined here. This is the main stage of UI design, which also includes a major part of UX design.</p>
            <div class="image-holder">
              <img src="../assets/images/wireframing.png" alt="#">
            </div>
            <div class="text-info">
              <span class="text-part">Goals:</span>
              <p>Creation of a working prototype that fully shows the logic and interface elements of your future product.</p>
            </div>
            <div class="block-info">
              <span class="block-title">What you should do at this stage:</span>
              <p class="block-text">Discuss the logic and wireframes, do research, introduce changes and test.</p>
            </div>
            <div class="block-info">
              <span class="block-title">What you should do at this stage:</span>
              <p class="block-text">Full understanding of the product logic, specification, opportunity to start backend development, a partial estimate (made for the product logic, which might include non-clarified requirements: additional visual effects, animations, custom interface elements, etc.), sources (in Figma or Adobe XD formats), prototype, list of all screens, interrelations of modules and screens in the product.</p>
            </div>
          </div>
          <div class="info-col column-4 order-3">
            <span class="subtitle">Design</span>
            <h3>Visualization</h3>
            <div class="block-container">
              <div class="text-cols">
                <div class="text-col">
                  <p>Final stage: visualization and branding of interface elements. Here your application gains visual individuality. Animations can be designed in EDGE and After Effects environments.</p>
                  <p>The cost of changes here are extremely high. The team provides you with a full estimate for development, based on the approved UI/UX design and project documentation.</p>
                  <p>It is important to involve a developer at this stage. This person must know how your product is going to work and what it is going to look like in order to suggest the least time-consuming methods for implementation that don't contradict its main goals and features. For example, implementation of a standard interface element takes significantly less time than implementation of a custom element. However, the visual differences might be minimal and not affect the logic and performance of your product.</p>
                </div>
                <div class="image-holder-mobile">
                  <img src="../assets/images/svg/visualization-mobile.svg" alt="#">
                </div>
                <div class="text-col">
                  <div class="text-info">
                    <span class="text-part">Goals:</span>
                    <p>Visualization and branding of the product according to your demands and modern UI/UX design trends that engage users.</p>
                  </div>
                  <div class="block-info">
                    <span class="block-title">What you should do at this stage:</span>
                    <p class="block-text">Decide on all the details, approve them, present your brand's visual identity.</p>
                  </div>
                  <div class="block-info">
                    <span class="block-title">What you should do at this stage:</span>
                    <p class="block-text">Source in PSD format, InVision prototype, full understanding of the logic and appearance of the product, full estimate for development.</p>
                  </div>
                </div>
              </div>
              <div class="image-holder">
                <img src="../assets/images/svg/visualization.svg" alt="#">
              </div>
            </div>
          </div>
          <div class="info-col column-2 order-4 col-more-link">
            <span class="subtitle">Validation</span>
            <h3>Validation and Verification design</h3>
            <p>"Validation" in the design process involves evaluating whether a design meets the intended user needs and objectives by testing it with real users or stakeholders to gather feedback and ensure usability and functionality. On the other hand, "Verification" focuses on confirming that the design aligns with the established requirements, standards, and specifications, ensuring that it meets technical and quality criteria as part of the design and development process.</p>
            <a
              href="/storage/app/media/5_process_styleguide.jpg" class="more-link"
              target="_blank">
              Explore example <img src="../assets/images/svg/more-link-icon.svg" alt="more-icon">
            </a>
          </div>
          <div class="info-col column-2 order-5 col-more-link">
            <span class="subtitle">Implementation</span>
            <h3>Preparations for Implementation</h3>
            <p>In addition to the assets mentioned above, the designer prepares a styleguide. It is a brief guideline with all fonts, colors, and elements used in the interface of your product.</p>
            <p>We also transfer ready-made and open for developers Figma or AdobeXD files with the marking of all elements and blocks. We create a cutting of icons and FAW icons of the application.</p>
            <p>We prepare marketing materials for publication on AppStore and PlayMarket.</p>
            <a
              href="/storage/app/media/5_process_styleguide.jpg" class="more-link"
              target="_blank">
              Explore example <img src="../assets/images/svg/more-link-icon.svg" alt="more-icon">
            </a>
          </div>
          <div class="info-col column-3 order-6 col-more-link">
            <div class="text-cols">
              <div class="text-col">
                <span class="subtitle">Develop</span>
                <h3>Testing</h3>
                <p>We test design on focus groups, conduct controlled and non-controlled user testing, A/B testing</p>
                <!-- <a
                  href="#" class="more-link"
                  target="_blank">
                  Explore example <img src="../assets/images/svg/more-link-icon.svg" alt="more-icon">
                </a> -->
              </div>
              <div class="text-col">
                <span class="subtitle">Develop</span>
                <h3>Support</h3>
                <p>We provide development support in terms of design throughout the product development cycle.</p>
                <!-- <a
                  href="#" class="more-link"
                  target="_blank">
                  Explore example <img src="../assets/images/svg/more-link-icon.svg" alt="more-icon">
                </a> -->
              </div>
            </div>
          </div>
          <div class="info-col column-1 order-7 col-more-link">
            <span class="subtitle">Review</span>
            <h3>Design Review</h3>
            <p>We offer and provide a full range of Design Audit and Design Review.</p>
            <a
              href="#" class="more-link"
              target="_blank">
              Explore example <img src="../assets/images/svg/more-link-icon.svg" alt="more-icon">
            </a>
          </div>
        </div>
        <div class="info-area">
          <h2>UI/UX Artifacts to Enhance Process</h2>
          <p class="description">The entire UX process is a tool for minimizing risk. <br /> By combining all or selected artifacts according to specific project’s needs we reduce the chance of wasting time optimize the cost structure and increase the overall product quality.</p>
        </div>
        <div class="accordion-holder">
          <div :class="['accordion', {'bottom-opened': isBottomAccordionItemOpened()}]" ref="accordion">
            <div v-for="(item, index) in accordionItems" :key="index"  :class="['accordion-item number-' + index, {'active': isAccordionItemOpen(index)}]">
              <div class="accordion-decor">
                <div class="accordion-header" @click="toggleAccordionItem(index)" :ref="'accordionItem_' + index">
                  <span class="item-number">{{ formatNumber(index + 1) }}</span>
                  {{ item.title }}
                </div>
              </div>
              <div class="accordion-content">
                <!-- <ul class="list-items">
                  <li v-for="(listItem, i) in item.listItems" :key="i">
                    {{ listItem }}
                  </li>
                </ul> -->
                <ul class="list-items">
                  <li v-for="(listItem, i) in item.listItems" :key="i">
                    <a
                      v-if="listItem.link"
                      :href="listItem.link"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="artifact-link">
                      {{ listItem.text }}
                    </a>
                    <span v-else>{{ listItem.text || listItem }}</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <div class="disclaimer-area">
            <div class="disclaimer-block">
            <span class="disclaimer-note"><i>*Disclaimer</i></span>
            <div class="disclaimer-text">
                <p>This is a complete list of artifacts that we use in the design. But this doesn’t mean that they are all mandatory for use on every project. Depending on the tasks, complexity of the project and the need to use, we apply them.</p>
                <!-- <a
                  href="/storage/app/media/5_process_styleguide.jpg" class="more-link"
                  target="_blank">
                  Explore Library <img src="../assets/images/svg/more-link-icon.svg" alt="more-icon">
                </a> -->
              </div>
            </div>
          </div>
        </div>
        <div class="contact-block">
          <h3>Next-Level UX/UI in Action</h3>
          <div class="contact-decor">
            <button @click="openFigma" class="contact-link">
            Explore Now
          </button>
          </div>
        </div>
        <div class="info-area">
          <h2>Methodologies Which We Use</h2>
          <p class="description">Depending on the complexity of the project and its features, we use the most suitable framework for this.</p>
          <ul class="metodologies-list">
            <li>
              <img src="../assets/images/svg/agile-illustration.svg" alt="agile illustration">
              <h4>Agile / Scrum</h4>
              <p>An approach that combines Agile development methods with the UX process. The project is divided into iterations, allowing you to quickly adapt to changes and receive feedback from users.</p>
            </li>
            <li>
              <img src="../assets/images/svg/design-thinking-illustration.svg" alt="design thinking illustration">
              <h4>Design Thinking</h4>
              <p>An innovative process aimed at solving problems and meeting the needs of users. Includes stages of compassion, problem identification, ideation, prototyping and testing.</p>
            </li>
            <li>
              <img src="../assets/images/svg/lean-ux-illustration.svg" alt="lean ux illustration">
              <h4>Lean UX</h4>
              <p>Focused on efficiency and minimizing costs. Work on the product is based on the rapid creation of hypotheses, rapid testing, iterations and making changes based on the results.</p>
            </li>
            <li>
              <img src="../assets/images/svg/waterfall-illustration.svg" alt="waterfall illustration">
              <h4>Waterfall</h4>
              <p>Traditional development methodology, where the project is divided into successive stages: requirements, design, implementation, testing and implementation. Each stage is completed before the next.</p>
            </li>
            <li>
              <img src="../assets/images/svg/human-centered-illustration.svg" alt="human centered design illustration">
              <h4>Human Centered Design</h4>
              <p>Places emphasis on the needs and desires of users throughout the design and development process.</p>
            </li>
            <li class="special-item">
              <p>These are<br /> five the most<br /> used<br /> frameworks<br /> for our<br /> projects</p>
            </li>
          </ul>
        </div>
        <div class="info-area">
          <h2>Individually Tailored Approach</h2>
          <p class="description">We offer even more flexibility to our customers. Understanding that every project is unique and every client has exclusive needs we combine various methodologies to build the optimal process.</p>
          <div class="framework-holder">
            <ul class="frameworks-list">
              <li class="special-item">Other frameworks that we use</li>
              <li>
                <h5>Double Diamond</h5>
                <p>Suitable for solving complex problems.</p>
              </li>
              <li>
                <h5>Iterative Design</h5>
                <p>Repetition of design cycles and product improvement based on user feedback.</p>
              </li>
              <li>
                <h5>Contextual Design</h5>
                <p>Includes consideration of the activity and context of user interaction with the product.</p>
              </li>
              <li>
                <h5>Google Ventures</h5>
                <p>Allows you to focus on solving a specific problem and quickly develop and test a prototype.</p>
              </li>
              <li class="decor-1">
                <h5>Rapid Prototyping</h5>
                <p>Repetition of design cycles and product improvement based on user feedback.</p>
              </li>
              <li>
                <h5>Atomic Design</h5>
                <p>Based on the idea of a systematic approach to design.</p>
              </li>
              <li>
                <h5>Service Design</h5>
                <p>Focuses on the design and improvement of services provided by the organization, with a user focus.</p>
              </li>
              <li class="decor-2">
                <h5>Experience Mapping</h5>
                <p>Creating a visual image of user interaction with a product or service.</p>
              </li>
              <li>
                <h5>Spiral</h5>
                <p>Divided into cycles, each of which includes planning stages, risk analysis, implementation and evaluation.</p>
              </li>
              <li>
                <h5>V-model</h5>
                <p>Each stage of development corresponds to the stage of verification  or validation of the results.</p>
              </li>
              <li>
                <h5>Big Design Up Front</h5>
                <p>An approach where all stages of design before developing a project are detailed and planned in advance.</p>
              </li>
              <li class="decor-3">
                <h5>Participatory Design</h5>
                <p>Includes active participation of users in the design process to implement their ideas and needs.</p>
              </li>
              <li>
                <h5>Co-Design</h5>
                <p>Collaborative design, where various stakeholders, including users, work together to create a product.</p>
              </li>
              <li>
                <h5>Experience Design</h5>
                <p>Focused on creating a comprehensive user experience with a product or service.</p>
              </li>
              <li>
                <h5>Game Design Thinking</h5>
                <p>Use of gamification elements to improve user experience.</p>
              </li>
              <li>
                <h5>Kanban</h5>
                <p>Methodology based on visualization of the process of developing and managing work tasks.</p>
              </li>
              <li>
                <h5>Six Sigma</h5>
                <p>Methodology to improve product quality by identifying and eliminating errors and unpredictable changes.</p>
              </li>
            </ul>
          </div>
        </div>
        <div class="contact-block">
          <h2>Let’s Work Together</h2>
          <div class="contact-decor">
            <button
              @click="showModal('ContactUsModal')"
              class="contact-link">
              Contact Us
            </button>
          </div>
        </div>
      </div><!--end content-->
      <Footer/>
    </div>
  </section>
</template>

<script>
import Footer from '@/components/UI/Footer';
import { mapState, mapActions, mapMutations } from 'vuex';

export default {
  computed: {
    ...mapState('pages/process', {
      background: state => state.background,
      content: state => state.content,
      visible: 'modalVisible',
      modalComponent: 'modalComponent',
    }),
  },
  created() {
    if (!this.background.url) {
      this.fetchProcessPageContent();
    }
  },
  data() {
    return {
      openAccordionIndex: 0,
      accordionItems: [
        {
          title: 'Research',
          type: 'research',
          listItems: [
            { text: 'Question Board' },
            { text: 'Stakeholder Interview', link: 'https://www.figma.com/proto/Vi0Ug4vuU1O7xmT5H2zAKh/UX-Methods-MD-Team-Library?node-id=4653-4640&viewport=-9614%2C-10%2C0.14&t=gHZFkB93xJuKAIr2-1&scaling=scale-down-width&content-scaling=fixed&starting-point-node-id=4653%3A4640&page-id=0%3A1&show-proto-sidebar=0' },
            { text: 'Online Survey', link: 'https://www.figma.com/proto/Vi0Ug4vuU1O7xmT5H2zAKh/UX-Methods-MD-Team-Library?node-id=4653-4638&viewport=-9614%2C-10%2C0.14&t=gHZFkB93xJuKAIr2-1&scaling=scale-down-width&content-scaling=fixed&starting-point-node-id=4653%3A4638&page-id=0%3A1&show-proto-sidebar=0' },
            { text: 'User Interview', link: 'https://www.figma.com/proto/Vi0Ug4vuU1O7xmT5H2zAKh/UX-Methods-MD-Team-Library?node-id=4653-4636&viewport=-11707%2C-133%2C0.18&t=NlfGEMdpDk2GtQhF-1&scaling=scale-down-width&content-scaling=fixed&starting-point-node-id=4653%3A4636&page-id=0%3A1&show-proto-sidebar=0' },
            { text: 'Data Analysis' },
            { text: 'Focus Groups', link: 'https://www.figma.com/proto/Vi0Ug4vuU1O7xmT5H2zAKh/UX-Methods-MD-Team-Library?node-id=4653-4639&viewport=-9614%2C-10%2C0.14&t=gHZFkB93xJuKAIr2-1&scaling=scale-down-width&content-scaling=fixed&starting-point-node-id=4653%3A4639&page-id=0%3A1&show-proto-sidebar=0' },
            { text: 'Requirement Analysis' },
            { text: 'Generation of Vision' },
            { text: 'Competitor Analysis', link: 'https://www.figma.com/proto/Vi0Ug4vuU1O7xmT5H2zAKh/UX-Methods-MD-Team-Library?node-id=4653-4642&viewport=-9614%2C-10%2C0.14&t=gHZFkB93xJuKAIr2-1&scaling=scale-down-width&content-scaling=fixed&starting-point-node-id=4653%3A4642&page-id=0%3A1&show-proto-sidebar=0' },
            { text: 'UX Mapping' },
            { text: 'Diary Studies', link: 'https://www.figma.com/proto/Vi0Ug4vuU1O7xmT5H2zAKh/UX-Methods-MD-Team-Library?node-id=4653-4641&viewport=-9614%2C-10%2C0.14&t=gHZFkB93xJuKAIr2-1&scaling=scale-down-width&content-scaling=fixed&starting-point-node-id=4653%3A4641&page-id=0%3A1&show-proto-sidebar=0' },
            { text: 'Field Study', link: 'https://www.figma.com/proto/Vi0Ug4vuU1O7xmT5H2zAKh/UX-Methods-MD-Team-Library?node-id=4653-4637&viewport=-9614%2C-10%2C0.14&t=gHZFkB93xJuKAIr2-1&scaling=scale-down-width&content-scaling=fixed&starting-point-node-id=4653%3A4637&page-id=0%3A1&show-proto-sidebar=0' },
            { text: 'User Testing on Competitors', link: 'https://www.figma.com/proto/Vi0Ug4vuU1O7xmT5H2zAKh/UX-Methods-MD-Team-Library?node-id=4653-4647&viewport=-9614%2C-10%2C0.14&t=gHZFkB93xJuKAIr2-1&scaling=scale-down-width&content-scaling=fixed&starting-point-node-id=4653%3A4647&page-id=0%3A1&show-proto-sidebar=0' },
            { text: 'Target Audience Analysis', link: 'https://www.figma.com/proto/Vi0Ug4vuU1O7xmT5H2zAKh/UX-Methods-MD-Team-Library?node-id=4653-4644&viewport=-9614%2C-10%2C0.14&t=gHZFkB93xJuKAIr2-1&scaling=scale-down-width&content-scaling=fixed&starting-point-node-id=4653%3A4644&page-id=0%3A1&show-proto-sidebar=0' },
          ],
        },
        {
          title: 'Analysis',
          type: 'analysis',
          listItems: [
            { text: 'Empathy Map', link: 'https://www.figma.com/proto/Vi0Ug4vuU1O7xmT5H2zAKh/UX-Methods-MD-Team-Library?node-id=4653-5985&viewport=-9614%2C-10%2C0.14&t=gHZFkB93xJuKAIr2-1&scaling=scale-down-width&content-scaling=fixed&starting-point-node-id=4653%3A5985&page-id=0%3A1&show-proto-sidebar=0' },
            { text: 'User Persona', link: 'https://www.figma.com/proto/Vi0Ug4vuU1O7xmT5H2zAKh/UX-Methods-MD-Team-Library?node-id=4653-5984&viewport=-9614%2C-10%2C0.14&t=gHZFkB93xJuKAIr2-1&scaling=scale-down-width&content-scaling=fixed&starting-point-node-id=4653%3A5984&page-id=0%3A1&show-proto-sidebar=0' },
            { text: 'CJM', link: 'https://www.figma.com/proto/Vi0Ug4vuU1O7xmT5H2zAKh/UX-Methods-MD-Team-Library?node-id=4653-5983&viewport=-9614%2C-10%2C0.14&t=gHZFkB93xJuKAIr2-1&scaling=scale-down-width&content-scaling=fixed&starting-point-node-id=4653%3A5983&page-id=0%3A1&show-proto-sidebar=0' },
            { text: 'User Stories', link: 'https://www.figma.com/proto/Vi0Ug4vuU1O7xmT5H2zAKh/UX-Methods-MD-Team-Library?node-id=4653-5990&viewport=-9614%2C-10%2C0.14&t=gHZFkB93xJuKAIr2-1&scaling=scale-down-width&content-scaling=fixed&starting-point-node-id=4653%3A5990&page-id=0%3A1&show-proto-sidebar=0' },
            { text: 'Job Stories', link: 'https://www.figma.com/proto/Vi0Ug4vuU1O7xmT5H2zAKh/UX-Methods-MD-Team-Library?node-id=4653-5989&viewport=-9614%2C-10%2C0.14&t=gHZFkB93xJuKAIr2-1&scaling=scale-down-width&content-scaling=fixed&starting-point-node-id=4653%3A5989&page-id=0%3A1&show-proto-sidebar=0' },
            { text: 'JTBD' },
            { text: 'Information Architecture' },
            { text: 'Screen Flows' },
            { text: 'Affinity Diagram', link: 'https://www.figma.com/proto/Vi0Ug4vuU1O7xmT5H2zAKh/UX-Methods-MD-Team-Library?node-id=4653-5986&viewport=-9614%2C-10%2C0.14&t=gHZFkB93xJuKAIr2-1&scaling=scale-down-width&content-scaling=fixed&starting-point-node-id=4653%3A5986&page-id=0%3A1&show-proto-sidebar=0' },
            { text: 'Value Stream', link: 'https://www.figma.com/proto/Vi0Ug4vuU1O7xmT5H2zAKh/UX-Methods-MD-Team-Library?node-id=4653-5987&viewport=-9614%2C-10%2C0.14&t=gHZFkB93xJuKAIr2-1&scaling=scale-down-width&content-scaling=fixed&starting-point-node-id=4653%3A5987&page-id=0%3A1&show-proto-sidebar=0' },
            { text: 'Lean Canvas', link: 'https://www.figma.com/proto/Vi0Ug4vuU1O7xmT5H2zAKh/UX-Methods-MD-Team-Library?node-id=4653-5982&viewport=-9614%2C-10%2C0.14&t=gHZFkB93xJuKAIr2-1&scaling=scale-down-width&content-scaling=fixed&starting-point-node-id=4653%3A5982&page-id=0%3A1&show-proto-sidebar=0' },
            { text: 'Analysis Context of Use', link: 'https://www.figma.com/proto/Vi0Ug4vuU1O7xmT5H2zAKh/UX-Methods-MD-Team-Library?node-id=4653-5988&viewport=-9614%2C-10%2C0.14&t=gHZFkB93xJuKAIr2-1&scaling=scale-down-width&content-scaling=fixed&starting-point-node-id=4653%3A5988&page-id=0%3A1&show-proto-sidebar=0' },
            { text: 'Situational Segments', link: 'https://www.figma.com/proto/Vi0Ug4vuU1O7xmT5H2zAKh/UX-Methods-MD-Team-Library?node-id=4653-5991&viewport=-9614%2C-10%2C0.14&t=gHZFkB93xJuKAIr2-1&scaling=scale-down-width&content-scaling=fixed&starting-point-node-id=4653%3A5991&page-id=0%3A1&show-proto-sidebar=0' },
          ],
        },
        {
          title: 'Ideation',
          listItems: [
            { text: 'Flow Diagram', link: 'https://www.figma.com/proto/Vi0Ug4vuU1O7xmT5H2zAKh/UX-Methods-MD-Team-Library?node-id=4653-5992&viewport=-9614%2C-10%2C0.14&t=gHZFkB93xJuKAIr2-1&scaling=scale-down-width&content-scaling=fixed&starting-point-node-id=4653%3A5992&page-id=0%3A1&show-proto-sidebar=0' },
            { text: 'Sitemap', link: 'https://www.figma.com/proto/Vi0Ug4vuU1O7xmT5H2zAKh/UX-Methods-MD-Team-Library?node-id=4653-5994&viewport=-9614%2C-10%2C0.14&t=gHZFkB93xJuKAIr2-1&scaling=scale-down-width&content-scaling=fixed&starting-point-node-id=4653%3A5994&page-id=0%3A1&show-proto-sidebar=0' },
            { text: 'Service Blueprints', link: 'https://www.figma.com/proto/Vi0Ug4vuU1O7xmT5H2zAKh/UX-Methods-MD-Team-Library?node-id=4653-5993&viewport=-9614%2C-10%2C0.14&t=gHZFkB93xJuKAIr2-1&scaling=scale-down-width&content-scaling=fixed&starting-point-node-id=4653%3A5993&page-id=0%3A1&show-proto-sidebar=0' },
            { text: 'Conversation Flow', link: 'https://www.figma.com/proto/Vi0Ug4vuU1O7xmT5H2zAKh/UX-Methods-MD-Team-Library?node-id=4653-6001&viewport=-9614%2C-10%2C0.14&t=gHZFkB93xJuKAIr2-1&scaling=scale-down-width&content-scaling=fixed&starting-point-node-id=4653%3A6001&page-id=0%3A1&show-proto-sidebar=0' },
            { text: 'Map of Sections and Screens', link: 'https://www.figma.com/proto/Vi0Ug4vuU1O7xmT5H2zAKh/UX-Methods-MD-Team-Library?node-id=4653-6000&viewport=-9614%2C-10%2C0.14&t=gHZFkB93xJuKAIr2-1&scaling=scale-down-width&content-scaling=fixed&starting-point-node-id=4653%3A6000&page-id=0%3A1&show-proto-sidebar=0' },
            { text: 'Concept Validation' },
            { text: 'BPMN', link: 'https://www.figma.com/proto/Vi0Ug4vuU1O7xmT5H2zAKh/UX-Methods-MD-Team-Library?node-id=4653-5996&viewport=-9614%2C-10%2C0.14&t=gHZFkB93xJuKAIr2-1&scaling=scale-down-width&content-scaling=fixed&starting-point-node-id=4653%3A5996&page-id=0%3A1&show-proto-sidebar=0' },
            { text: 'Object Models', link: 'https://www.figma.com/proto/Vi0Ug4vuU1O7xmT5H2zAKh/UX-Methods-MD-Team-Library?node-id=4653-5997&viewport=-9614%2C-10%2C0.14&t=gHZFkB93xJuKAIr2-1&scaling=scale-down-width&content-scaling=fixed&starting-point-node-id=4653%3A5997&page-id=0%3A1&show-proto-sidebar=0' },
            { text: 'Ecosystem Map', link: 'https://www.figma.com/proto/Vi0Ug4vuU1O7xmT5H2zAKh/UX-Methods-MD-Team-Library?node-id=4653-5998&viewport=-9614%2C-10%2C0.14&t=gHZFkB93xJuKAIr2-1&scaling=scale-down-width&content-scaling=fixed&starting-point-node-id=4653%3A5998&page-id=0%3A1&show-proto-sidebar=0' },
            { text: 'User Story Map', link: 'https://www.figma.com/proto/Vi0Ug4vuU1O7xmT5H2zAKh/UX-Methods-MD-Team-Library?node-id=4653-5995&viewport=-9614%2C-10%2C0.14&t=gHZFkB93xJuKAIr2-1&scaling=scale-down-width&content-scaling=fixed&starting-point-node-id=4653%3A5995&page-id=0%3A1&show-proto-sidebar=0' },
            { text: 'Conceptual Model of Roles Interaction', link: 'https://www.figma.com/proto/Vi0Ug4vuU1O7xmT5H2zAKh/UX-Methods-MD-Team-Library?node-id=4653-5999&viewport=-9614%2C-10%2C0.14&t=gHZFkB93xJuKAIr2-1&scaling=scale-down-width&content-scaling=fixed&starting-point-node-id=4653%3A5999&page-id=0%3A1&show-proto-sidebar=0' },
          ],
        },
        {
          title: 'Design',
          listItems: [
            { text: 'Mood Board', link: 'https://www.figma.com/proto/Vi0Ug4vuU1O7xmT5H2zAKh/UX-Methods-MD-Team-Library?node-id=4653-7315&viewport=-9614%2C-10%2C0.14&t=gHZFkB93xJuKAIr2-1&scaling=scale-down-width&content-scaling=fixed&starting-point-node-id=4653%3A7315&page-id=0%3A1&show-proto-sidebar=0' },
            { text: 'Sketches / Low Fidelity Wireframes ', link: 'https://www.figma.com/proto/Vi0Ug4vuU1O7xmT5H2zAKh/UX-Methods-MD-Team-Library?node-id=4653-7307&viewport=-9614%2C-10%2C0.14&t=gHZFkB93xJuKAIr2-1&scaling=scale-down-width&content-scaling=fixed&starting-point-node-id=4653%3A7307&page-id=0%3A1&show-proto-sidebar=0' },
            { text: 'High Fidelity Wireframes', link: 'https://www.figma.com/proto/Vi0Ug4vuU1O7xmT5H2zAKh/UX-Methods-MD-Team-Library?node-id=4653-7308&viewport=-9614%2C-10%2C0.14&t=gHZFkB93xJuKAIr2-1&scaling=scale-down-width&content-scaling=fixed&starting-point-node-id=4653%3A7308&page-id=0%3A1&show-proto-sidebar=0' },
            { text: 'Color Scheme', link: 'https://www.figma.com/proto/Vi0Ug4vuU1O7xmT5H2zAKh/UX-Methods-MD-Team-Library?node-id=4653-7317&viewport=-9614%2C-10%2C0.14&t=gHZFkB93xJuKAIr2-1&scaling=scale-down-width&content-scaling=fixed&starting-point-node-id=4653%3A7317&page-id=0%3A1&show-proto-sidebar=0' },
            { text: 'Fonts Scheme', link: 'https://www.figma.com/proto/Vi0Ug4vuU1O7xmT5H2zAKh/UX-Methods-MD-Team-Library?node-id=4653-7314&viewport=-9614%2C-10%2C0.14&t=gHZFkB93xJuKAIr2-1&scaling=scale-down-width&content-scaling=fixed&starting-point-node-id=4653%3A7314&page-id=0%3A1&show-proto-sidebar=0' },
            { text: 'Design Concept', link: 'https://www.figma.com/proto/Vi0Ug4vuU1O7xmT5H2zAKh/UX-Methods-MD-Team-Library?node-id=4653-7316&viewport=-9614%2C-10%2C0.14&t=gHZFkB93xJuKAIr2-1&scaling=scale-down-width&content-scaling=fixed&starting-point-node-id=4653%3A7316&page-id=0%3A1&show-proto-sidebar=0' },
            { text: 'Prototyping', link: 'https://www.figma.com/proto/Vi0Ug4vuU1O7xmT5H2zAKh/UX-Methods-MD-Team-Library?node-id=4653-7310&viewport=-9614%2C-10%2C0.14&t=gHZFkB93xJuKAIr2-1&scaling=scale-down-width&content-scaling=fixed&starting-point-node-id=4653%3A7310&page-id=0%3A1&show-proto-sidebar=0' },
            { text: 'High Fidelity Mockups', link: 'https://www.figma.com/proto/Vi0Ug4vuU1O7xmT5H2zAKh/UX-Methods-MD-Team-Library?node-id=4653-7309&viewport=-9614%2C-10%2C0.14&t=gHZFkB93xJuKAIr2-1&scaling=scale-down-width&content-scaling=fixed&starting-point-node-id=4653%3A7309&page-id=0%3A1&show-proto-sidebar=0' },
            { text: 'Preparation for Implementation', link: 'https://www.figma.com/proto/Vi0Ug4vuU1O7xmT5H2zAKh/UX-Methods-MD-Team-Library?node-id=4653-7312&viewport=-9614%2C-10%2C0.14&t=gHZFkB93xJuKAIr2-1&scaling=scale-down-width&content-scaling=fixed&starting-point-node-id=4653%3A7312&page-id=0%3A1&show-proto-sidebar=0' },
            { text: 'Preparation for Publication', link: 'https://www.figma.com/proto/Vi0Ug4vuU1O7xmT5H2zAKh/UX-Methods-MD-Team-Library?node-id=4653-7313&viewport=-9614%2C-10%2C0.14&t=gHZFkB93xJuKAIr2-1&scaling=scale-down-width&content-scaling=fixed&starting-point-node-id=4653%3A7313&page-id=0%3A1&show-proto-sidebar=0' },
            { text: 'Design System', link: 'https://www.figma.com/proto/Vi0Ug4vuU1O7xmT5H2zAKh/UX-Methods-MD-Team-Library?node-id=4653-7320&viewport=-9614%2C-10%2C0.14&t=gHZFkB93xJuKAIr2-1&scaling=scale-down-width&content-scaling=fixed&starting-point-node-id=4653%3A7320&page-id=0%3A1&show-proto-sidebar=0' },
            { text: 'UI Kit', link: 'https://www.figma.com/proto/Vi0Ug4vuU1O7xmT5H2zAKh/UX-Methods-MD-Team-Library?node-id=4653-7321&viewport=-9614%2C-10%2C0.14&t=gHZFkB93xJuKAIr2-1&scaling=scale-down-width&content-scaling=fixed&starting-point-node-id=4653%3A7321&page-id=0%3A1&show-proto-sidebar=0' },
          ],
        },
        {
          title: 'Validation',
          listItems: [
            { text: 'Design Review', link: 'https://www.figma.com/proto/Vi0Ug4vuU1O7xmT5H2zAKh/UX-Methods-MD-Team-Library?node-id=4653-4648&viewport=-9614%2C-10%2C0.14&t=gHZFkB93xJuKAIr2-1&scaling=scale-down-width&content-scaling=fixed&starting-point-node-id=4653%3A4648&page-id=0%3A1&show-proto-sidebar=0' },
            { text: 'Usability Testing' },
            { text: 'Shadowing' },
            { text: 'A/B Testing', link: 'https://www.figma.com/proto/Vi0Ug4vuU1O7xmT5H2zAKh/UX-Methods-MD-Team-Library?node-id=4653-4650&viewport=-9614%2C-10%2C0.14&t=gHZFkB93xJuKAIr2-1&scaling=scale-down-width&content-scaling=fixed&starting-point-node-id=4653%3A4650&page-id=0%3A1&show-proto-sidebar=0' },
            { text: 'Heuristic Evaluation' },
            { text: 'QA' },
            { text: 'Analytics', link: 'https://www.figma.com/proto/Vi0Ug4vuU1O7xmT5H2zAKh/UX-Methods-MD-Team-Library?node-id=4653-4646&viewport=-9614%2C-10%2C0.14&t=gHZFkB93xJuKAIr2-1&scaling=scale-down-width&content-scaling=fixed&starting-point-node-id=4653%3A4646&page-id=0%3A1&show-proto-sidebar=0' },
            { text: 'Desirability Studies', link: 'https://www.figma.com/proto/Vi0Ug4vuU1O7xmT5H2zAKh/UX-Methods-MD-Team-Library?node-id=4653-4651&viewport=-9614%2C-10%2C0.14&t=gHZFkB93xJuKAIr2-1&scaling=scale-down-width&content-scaling=fixed&starting-point-node-id=4653%3A4651&page-id=0%3A1&show-proto-sidebar=0' },
            { text: 'Metrics' },
            { text: 'Eye Tracking', link: 'https://www.figma.com/proto/Vi0Ug4vuU1O7xmT5H2zAKh/UX-Methods-MD-Team-Library?node-id=4653-4645&viewport=-9614%2C-10%2C0.14&t=gHZFkB93xJuKAIr2-1&scaling=scale-down-width&content-scaling=fixed&starting-point-node-id=4653%3A4645&page-id=0%3A1&show-proto-sidebar=0' },
          ],
        },
        {
          title: 'Development',
          listItems: [
            { text: 'Developer Handover' },
            { text: 'BETA Launch' },
            { text: 'User Testing', link: 'https://www.figma.com/proto/Vi0Ug4vuU1O7xmT5H2zAKh/UX-Methods-MD-Team-Library?node-id=4653-4643&viewport=-9614%2C-10%2C0.14&t=gHZFkB93xJuKAIr2-1&scaling=scale-down-width&content-scaling=fixed&starting-point-node-id=4653%3A4643&page-id=0%3A1&show-proto-sidebar=0' },
            { text: 'Release/Launch' },
            { text: 'Support' },
          ],
        },
      ],
      selectedWorkflowIndex: 0,
      workflowItems: [
        { title: 'Research and Analysis', listItems: ['Requirements analysis', 'Competitor analysis and research of TA', 'Estimating'], marker: 'UX Design' },
        { title: 'Ideation', listItems: ['Screens map and User Flow', 'Wireframes and Prototyping'] },
        { title: 'Design', listItems: ['Design concept development', 'Color scheme and Font selection', 'Interface creation and Prototyping'], marker: 'UI Design' },
        { title: 'Validation', listItems: ['Validation and verification'] },
        { title: 'Implementation', listItems: ['UI kit and preparation for implementation', 'Preparation for publishing'] },
        { title: 'Develop', listItems: ['Testing', 'Support'], marker: 'Development' },
        { title: 'Review', listItems: ['Design Review'] },
      ],
    };
  },
  methods: {
    ...mapActions({ fetchProcessPageContent: 'pages/process/process:fetch' }),
    ...mapMutations(['showModal']),
    toggleAccordionItem(index) {
      if (this.openAccordionIndex === index) {
        this.openAccordionIndex = null;
      } else {
        this.openAccordionIndex = index;
        this.scrollToActiveAccordionItem(index);
      }
    },
    isAccordionItemOpen(index) {
      return this.openAccordionIndex === index;
    },
    isBottomAccordionItemOpened() {
      return this.openAccordionIndex > 2;
    },
    scrollToActiveAccordionItem(index) {
      const refName = `accordionItem_${index}`;
      const activeAccordionItem = this.$refs[refName][0];
      if (activeAccordionItem) {
        setTimeout(() => {
          const rect = activeAccordionItem.getBoundingClientRect();
          window.scrollTo({
            top: rect.top + window.scrollY,
            behavior: 'smooth',
          });
        }, 0);
      }
    },
    selectWorkflowItem(index) {
      this.selectedWorkflowIndex = index;
    },
    formatNumber(number) {
      return number < 10 ? `0${number}` : `${number}`;
    },
    openFigma() {
      window.open('https://www.figma.com/proto/Vi0Ug4vuU1O7xmT5H2zAKh/UX-Methods-MD-team-library?node-id=427-3540&p=f&viewport=546%2C146%2C0.11&t=EkBAjKQtgHf4sS9B-1&scaling=scale-down-width&content-scaling=fixed&starting-point-node-id=427%3A3540&page-id=0%3A1https://www.figma.com/proto/Vi0Ug4vuU1O7xmT5H2zAKh/UX-Methods-MD-team-library?node-id=427-3540&p=f&viewport=546%2C146%2C0.11&t=EkBAjKQtgHf4sS9B-1&scaling=scale-down-width&content-scaling=fixed&starting-point-node-id=427%3A3540&page-id=0%3A1', '_blank');
    },
  },
  components: {
    Footer,
  },
};
</script>



// WEBPACK FOOTER //
// src/views/Process.vue